import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

/* ── Types ── */

/** Espelha `OnboardingStepCode` (domain/entities/onboarding-plan.entity.ts). */
export type OnboardingStep =
  | 'PENDING'
  | 'CHECKLIST'
  | 'IMPORT'
  | 'SIMULATION'
  | 'ACTIVE'
  | 'GO_LIVE'
  | 'REVIEW_7'
  | 'REVIEW_30'
  | 'REVIEW_60';

/** Espelha `OnboardingStepDto`. */
export interface OnboardingStepState {
  code: OnboardingStep;
  index: number;
  done: boolean;
  current: boolean;
  /** O agregado aceita transitar da etapa atual para esta. */
  reachable: boolean;
}

/**
 * Espelha `OnboardingPlanResponseDto`, campo a campo.
 *
 * O tipo anterior declarava `checklist`, `assignedToId`, `notes` e mais seis
 * campos que a rota **nunca devolveu**: `GET /onboarding/:companyId` respondia
 * `{ progress, status, currentStep }` e nada mais. O handler não declarava
 * tipo de retorno, então `check:api` contava a rota como "sem handler" e não
 * comparava campo nenhum.
 */
export interface OnboardingPlan {
  id: string | null;
  tenantId: string | null;
  companyId: string;
  progress: number;
  status: OnboardingStep | 'NOT_FOUND';
  currentStep: OnboardingStep;
  steps: OnboardingStepState[];
  exists: boolean;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Espelha `OnboardingTransitionResponseDto`. */
export interface OnboardingTransitionResult {
  message: string;
  currentStep: OnboardingStep;
}

/** Rótulos das etapas, na linguagem do domínio. */
export const STEP_LABEL: Record<OnboardingStep, string> = {
  PENDING: 'Cadastro inicial',
  CHECKLIST: 'Checklist de configuração',
  IMPORT: 'Importação de cadastros',
  SIMULATION: 'Simulação e homologação',
  ACTIVE: 'Operação assistida',
  GO_LIVE: 'Go-live',
  REVIEW_7: 'Revisão de 7 dias',
  REVIEW_30: 'Revisão de 30 dias',
  REVIEW_60: 'Revisão de 60 dias',
};

/** O que cada etapa exige, para a faixa "próximo passo" dizer algo útil. */
export const STEP_HINT: Record<OnboardingStep, string> = {
  PENDING: 'Confira os dados da empresa e abra o plano de implantação.',
  CHECKLIST: 'Configure tributos, contas bancárias e usuários antes de importar dados.',
  IMPORT: 'Importe clientes, produtos e saldos iniciais em Dados Mestres.',
  SIMULATION: 'Emita documentos em homologação e confira o cálculo no Simulador de Impacto.',
  ACTIVE: 'Opere acompanhado: as rotinas já valem, com conferência diária.',
  GO_LIVE: 'Operação liberada. As revisões acompanham os primeiros 60 dias.',
  REVIEW_7: 'Primeira revisão: conferir fechamento fiscal e financeiro da semana.',
  REVIEW_30: 'Revisão de 30 dias: conciliação bancária e apuração do mês.',
  REVIEW_60: 'Revisão de 60 dias: encerramento do acompanhamento de implantação.',
};

/* ── Hooks ── */

export function useOnboardingPlan(companyId: string) {
  return useQuery({
    queryKey: ['onboarding', companyId] as const,
    queryFn: () => apiClient.get<OnboardingPlan>(`/onboarding/${companyId}`),
    enabled: !!companyId,
  });
}

export function useCreateOnboardingPlan() {
  const qc = useQueryClient();
  return useMutation({
    /* `POST /create` devolve o plano na mesma forma da consulta — não é
       preciso uma segunda chamada para saber o estado inicial. */
    mutationFn: (companyId: string) =>
      apiClient.post<OnboardingPlan>(`/onboarding/${companyId}/create`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

export function useTransitionOnboardingStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, step }: { companyId: string; step: OnboardingStep }) =>
      apiClient.post<OnboardingTransitionResult>(`/onboarding/${companyId}/transition`, { step }),
    onSuccess: (_, { companyId }) => {
      qc.invalidateQueries({ queryKey: ['onboarding', companyId] });
    },
  });
}

export function useGoLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) =>
      apiClient.post<OnboardingTransitionResult>(`/onboarding/${companyId}/go-live`),
    onSuccess: (_, companyId) => {
      qc.invalidateQueries({ queryKey: ['onboarding', companyId] });
    },
  });
}
